/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';

/* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
@Component('footerTagline', {
    name: 'Footer Tagline',
    description: 'Large brand tagline with a per-word highlight band (ticker-tape) behind each word.',
    group: '4_BlackMagic_Footer',
})
@RegionDefinition([])
export class FooterTaglineMetadata {
    @AttributeDefinition({
        id: 'primaryText',
        name: 'Text',
        type: 'string',
        required: true,
        defaultValue: 'Build Living Soil',
    })
    primaryText?: string;

    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Highlight Color',
        description: 'Brand highlight band shown behind each word. "none" leaves the words on the bare background.',
        type: 'enum',
        values: ['blue', 'green', 'orange', 'none'],
        defaultValue: 'blue',
    })
    backgroundColor?: string;
}
/* v8 ignore stop */

// Per-line left indents (in em, so they scale with the font) — mimics the Figma cascade where
// the words don't share a left edge (e.g. Build / Living / Soil ≈ 1em / 0 / 2em).
const WORD_INDENTS = ['1em', '0em', '2em'];

/** Highlight-color option → the design-system highlight class (tokens/highlight.css). */
const HIGHLIGHT_CLASS: Record<string, string> = {
    blue: 'highlight-blue',
    green: 'highlight-green',
    orange: 'highlight-orange',
};

export interface FooterTaglineProps {
    primaryText?: string;
    /** Brand highlight band behind each word (`blue` | `green` | `orange` | `none`). Defaults to `blue`. */
    backgroundColor?: string;
}

export default function FooterTagline({
    primaryText,
    backgroundColor = 'blue',
}: FooterTaglineProps): ReactElement | null {
    if (!primaryText?.trim()) return null;
    // Each word on its own line, staggered to mimic the design, and the whole block rotated by a
    // single angle — the middle of the design's per-word angles (≈ -1.2° / -2.2° / -1.2°). Each word
    // gets a brand highlight band behind it (ticker-tape look); the highlight class sets the band
    // color + organic-white text (tokens/highlight.css). `backgroundColor: 'none'` keeps bare words.
    const words = primaryText.trim().split(/\s+/);
    const highlight = HIGHLIGHT_CLASS[backgroundColor];
    return (
        <p className="inline-block -rotate-[1.5deg] font-display font-extrabold uppercase tracking-[-0.03em] text-6xl text-[var(--bm-organic-white)] md:text-8xl">
            {words.map((word, index) => (
                // Outer line: `leading-none` collapses the line box so the band hugs the word, the
                // stagger `marginLeft` offsets it, and `mb-*` opens a real gap between the word bands.
                <span
                    key={word}
                    className="block leading-none mb-[0.04em]"
                    style={{ marginLeft: WORD_INDENTS[index % WORD_INDENTS.length] }}>
                    {/* Inner band: an inline-block so the padding shows on all four sides; `text-box-trim`
                        trims the line box to the cap height so the band sits evenly top/bottom. */}
                    <span
                        className={cn(
                            'inline-block leading-none',
                            highlight &&
                                cn(
                                    highlight,
                                    '[text-box-trim:trim-both] [text-box-edge:cap_alphabetic] pl-[0.2em] pr-[0.05em] py-[0.16em] shadow-sm'
                                )
                        )}>
                        {word}
                    </span>
                </span>
            ))}
        </p>
    );
}
